import React, { useEffect, useRef, useState } from "react"
import { View, ScrollView, Dimensions } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import styled from "~/ui/styled"
import { Text, CenterFlex, ColumnFlex, Button } from "~components/El"

const SCREEN_HEIGHT = Dimensions.get("window").height
const ITEM_HEIGHT = 48
const ITEM_GAP = 6

const TitleBar = styled(CenterFlex)`
  width: 100%;
  height: auto;
  min-height: 52px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  box-sizing: border-box;
`

const OptionButton = styled(Button)`
  width: 100%;
  min-height: ${ITEM_HEIGHT}px;
  height: auto;
  padding: 0;
  margin-bottom: ${ITEM_GAP}px;
  flex-shrink: 0;
`

const OptionRow = styled(CenterFlex)<{ $active?: boolean }>`
  width: 100%;
  min-height: ${ITEM_HEIGHT}px;
  padding: 0 16px;
  border-radius: 8px;
  box-sizing: border-box;
  background-color: ${({ $active }) => ($active ? "rgba(7, 193, 96, 0.1)" : "transparent")};
`

type PickerOption = {
  label: string
  value: number | string
}

type PickerProps = {
  value?: number | string
  onChange?: (value: number | string, item?: PickerOption, props?: PickerProps) => void
  title?: string
  options?: PickerOption[]
  renderItem?: (info: { value: number | string; item: PickerOption; props: PickerProps }) => React.ReactNode
}

const Picker = (props: PickerProps) => {
  const { value, onChange, title = "", options = [], renderItem = null } = props
  const insets = useSafeAreaInsets()
  const scrollRef = useRef<HTMLDivElement>(null)
  const positions = useRef<Record<number, number>>({})
  const activeIndex = options.findIndex((item) => item.value === value)

  const listHeight = Math.min(
    options.length * (ITEM_HEIGHT + ITEM_GAP) + 8,
    SCREEN_HEIGHT * 0.45,
  )
  const [data, setData] = useState(value)

  useEffect(() => {
    setData(value)
  }, [value])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const el = scrollRef.current
      if (!el || activeIndex < 0) return

      const itemY = positions.current[activeIndex] ?? activeIndex * (ITEM_HEIGHT + ITEM_GAP)
      const target = itemY - listHeight / 2 + ITEM_HEIGHT / 2
      el.scrollTop = Math.max(target, 0)
    }, 50)

    return () => window.clearTimeout(timer)
  }, [activeIndex, listHeight])

  return (
    <ColumnFlex width="100%" style={{ minWidth: 0 }}>
      <TitleBar>
        <Text color="text" fontWeight="600" fontSize="18px">
          {title}
        </Text>
      </TitleBar>

      <View style={{ width: "100%", maxHeight: listHeight, minHeight: Math.min(listHeight, ITEM_HEIGHT * 3) }}>
        <ScrollView
          ref={scrollRef}
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
          style={{ width: "100%", maxHeight: listHeight }}
          contentContainerStyle={{
            padding: "8px 12px",
            paddingBottom: insets.bottom + 16,
            boxSizing: "border-box",
          }}
        >
          {options.map((item, index) => {
            const active = item.value === data
            return (
              <View
                key={`${item.value}-${index}`}
                onLayout={(e) => {
                  positions.current[index] = e.nativeEvent.layout.y
                }}
              >
                <OptionButton
                  onClick={() => {
                    setData(item.value)
                    onChange?.(item.value, item, props)
                  }}
                >
                  {renderItem ? (
                    renderItem({
                      value: item.value,
                      item,
                      props,
                    })
                  ) : (
                    <OptionRow $active={active}>
                      <Text
                        color={active ? "primary" : "secondaryText"}
                        fontSize="16px"
                        fontWeight={active ? 600 : 400}
                      >
                        {item.label}
                      </Text>
                    </OptionRow>
                  )}
                </OptionButton>
              </View>
            )
          })}
        </ScrollView>
      </View>
    </ColumnFlex>
  )
}

export default Picker
